import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield, Users, Heart, AlertTriangle, Scale, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900/20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)}
                className="p-2 rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Terms of Service
                </h1>
                <p className="text-muted-foreground">
                  Last updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Introduction */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="w-5 h-5 text-purple-600" />
                    Welcome to Positivio
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    Welcome to Positivio, a platform dedicated to spreading positivity, building supportive communities, 
                    and connecting real humans in meaningful ways. By using our service, you agree to these terms.
                  </p>
                  <p>
                    Our mission is to create a safe, authentic space where verified humans can share experiences, 
                    support each other, and build genuine friendships.
                  </p>
                </CardContent>
              </Card>

              {/* Community Guidelines */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Community Guidelines
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-pink-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Be Kind and Respectful</h4>
                        <p className="text-sm text-muted-foreground">
                          Treat all community members with kindness, empathy, and respect. We have zero tolerance for harassment, bullying, or hate speech.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Shield className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Stay Authentic</h4>
                        <p className="text-sm text-muted-foreground">
                          Our verified human system exists to maintain authenticity. Fake accounts, impersonation, or deceptive practices are prohibited.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Share Positively</h4>
                        <p className="text-sm text-muted-foreground">
                          Focus on uplifting content. While we understand life has challenges, aim to contribute constructively to discussions.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Verification System */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    Human Verification System
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    Positivio requires human verification to maintain authenticity and trust within our community.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Verification is required for full platform access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Multiple verification methods available (photo ID, selfie video, phone verification)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Verification data is securely stored and not shared with third parties</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Verified status may be revoked for policy violations</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Prohibited Content */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Prohibited Content & Behavior
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>The following content and behaviors are strictly prohibited:</p>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <ul className="space-y-2">
                      <li>• Harassment, bullying, or threats</li>
                      <li>• Hate speech or discrimination</li>
                      <li>• Spam or excessive self-promotion</li>
                      <li>• Misinformation or false content</li>
                      <li>• Adult or inappropriate content</li>
                    </ul>
                    <ul className="space-y-2">
                      <li>• Copyright infringement</li>
                      <li>• Doxxing or sharing private information</li>
                      <li>• Impersonation or fake accounts</li>
                      <li>• Illegal activities or content</li>
                      <li>• Attempts to circumvent verification</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Privacy & Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Privacy & Data Protection
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    Your privacy is important to us. We collect and use data as outlined in our Privacy Policy:
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>We only collect necessary data for platform functionality</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Verification data is encrypted and securely stored</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>We never sell or share personal data with third parties</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>You can request data deletion at any time</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Account Termination */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="w-5 h-5 text-amber-600" />
                    Account Termination
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    We reserve the right to suspend or terminate accounts that violate these terms:
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• First violation: Warning and content removal</li>
                    <li>• Repeated violations: Temporary suspension</li>
                    <li>• Severe violations: Permanent account termination</li>
                    <li>• You may appeal decisions through our support system</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Changes to Terms */}
              <Card>
                <CardHeader>
                  <CardTitle>Changes to Terms</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">
                    We may update these terms occasionally. Users will be notified of significant changes 
                    via email or platform notification. Continued use of the platform constitutes acceptance 
                    of updated terms.
                  </p>
                </CardContent>
              </Card>

              {/* Contact */}
              <Card>
                <CardHeader>
                  <CardTitle>Contact Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">
                    Questions about these terms? Contact us at{" "}
                    <span className="font-semibold text-purple-600">legal@positivio.com</span>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TermsOfService;